import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Phone } from 'lucide-react';
import { enquiriesApi } from '../services/resources';
import { getApiErrorMessage } from '../services/api';
import { ILMSPageHeader } from '../components/ILMSPageHeader';
import { ILMSCard } from '../components/ILMSCard';
import { ILMSButton } from '../components/ILMSButton';
import { ILMSAlert } from '../components/ILMSAlert';
import { ILMSStatusBadge } from '../components/ILMSStatusBadge';
import { ILMSTable, type ILMSTableColumn } from '../components/ILMSTable';
import { ILMSFilterBar } from '../components/ILMSFilterBar';
import { ILMSSelect, ILMSTextarea, ILMSFormField } from '../components/ILMSForm';
import { ILMSModal } from '../components/ILMSModal';
import { DEFAULT_PAGE_SIZE, ILMSPagination } from '../components/ILMSPagination';
import { formatDateTime } from '../utils/format';
import type { EnquiryStatus, PublicEnquiry } from '../types';

// Contact-form submissions from the public website (website/) — staff triage
// them here: New → Contacted → Closed, with a notes field for what was said.

const STATUS_OPTIONS: EnquiryStatus[] = ['New', 'Contacted', 'Closed'];

export default function EnquiriesPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<EnquiryStatus | ''>('');
  const [selected, setSelected] = useState<PublicEnquiry | null>(null);
  const [draftStatus, setDraftStatus] = useState<EnquiryStatus>('New');
  const [draftNotes, setDraftNotes] = useState('');

  const listQuery = useQuery({
    queryKey: ['enquiries', page, pageSize, statusFilter],
    queryFn: () => enquiriesApi.list({ page, page_size: pageSize, status_filter: statusFilter || undefined } as any),
  });

  const updateMutation = useMutation({
    mutationFn: () => enquiriesApi.update(selected!.id, { status: draftStatus, staff_notes: draftNotes } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enquiries'] });
      setSelected(null);
    },
  });

  const openDetail = (row: PublicEnquiry) => {
    setSelected(row);
    setDraftStatus(row.status);
    setDraftNotes(row.staff_notes ?? '');
  };

  const columns: ILMSTableColumn<PublicEnquiry>[] = [
    { key: 'date', header: 'Received', primary: true, render: (r) => formatDateTime(r.created_at) },
    { key: 'name', header: 'Name', render: (r) => r.name },
    { key: 'contact', header: 'Contact', render: (r) => (
      <div className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1 text-secondary text-ink-700"><Mail className="h-3 w-3" /> {r.email}</span>
        {r.phone && <span className="flex items-center gap-1 text-secondary text-ink-500"><Phone className="h-3 w-3" /> {r.phone}</span>}
      </div>
    ) },
    { key: 'subject', header: 'Subject', render: (r) => r.subject || '—', hideOnTablet: true },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ];

  return (
    <div className="flex flex-col gap-4">
      <ILMSPageHeader title="Enquiries" subtitle="Contact-form submissions from the public website." />

      <ILMSCard padded={false}>
        <div className="p-4 sm:p-5 border-b border-ink-300/10">
          <ILMSFilterBar activeCount={statusFilter ? 1 : 0} onClear={() => { setStatusFilter(''); setPage(1); }}>
            <ILMSSelect value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as EnquiryStatus | ''); setPage(1); }} className="w-full lg:w-52">
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </ILMSSelect>
          </ILMSFilterBar>
        </div>
        <div className="p-4 sm:p-5">
          <ILMSTable
            columns={columns}
            data={listQuery.data?.items ?? []}
            rowKey={(r) => r.id}
            onRowClick={openDetail}
            exportTitle="Enquiries" exportDateOf={(r) => r.created_at}
            exportRows={async () => (await enquiriesApi.list({ page: 1, page_size: 2000, status_filter: statusFilter || undefined } as any)).items}
            isLoading={listQuery.isLoading}
            error={listQuery.isError ? getApiErrorMessage(listQuery.error) : null}
            onRetry={() => listQuery.refetch()}
            emptyTitle="No enquiries yet."
            emptyDescription="Submissions from the public website's contact form will appear here."
          />
          {!listQuery.isLoading && !listQuery.isError && (listQuery.data?.total ?? 0) > 0 && (
            <ILMSPagination page={page} pageSize={pageSize} total={listQuery.data?.total ?? 0} onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
          )}
        </div>
      </ILMSCard>

      <ILMSModal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `Enquiry from ${selected.name}` : ''}
        size="lg"
        footer={
          <>
            <ILMSButton variant="ghost" onClick={() => setSelected(null)}>Cancel</ILMSButton>
            <ILMSButton onClick={() => updateMutation.mutate()} loading={updateMutation.isPending}>Save</ILMSButton>
          </>
        }
      >
        {selected && (
          <div className="flex flex-col gap-4">
            {updateMutation.isError && <ILMSAlert tone="critical">{getApiErrorMessage(updateMutation.error, 'Unable to save changes.')}</ILMSAlert>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-secondary">
              <div><span className="text-ink-400">Email:</span> {selected.email}</div>
              <div><span className="text-ink-400">Phone:</span> {selected.phone || '—'}</div>
              <div><span className="text-ink-400">Subject:</span> {selected.subject || '—'}</div>
              <div><span className="text-ink-400">Received:</span> {formatDateTime(selected.created_at)}</div>
            </div>
            <ILMSFormField label="Message">
              <p className="text-body text-ink-900 bg-surface-subtle rounded-md p-3 whitespace-pre-wrap">{selected.message}</p>
            </ILMSFormField>
            <ILMSFormField label="Status">
              <ILMSSelect value={draftStatus} onChange={(e) => setDraftStatus(e.target.value as EnquiryStatus)}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </ILMSSelect>
            </ILMSFormField>
            <ILMSFormField label="Staff Notes" optional>
              <ILMSTextarea value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} placeholder="What was discussed / follow-up needed…" />
            </ILMSFormField>
          </div>
        )}
      </ILMSModal>
    </div>
  );
}
